/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1684972800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu May 25 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "s3-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "AWS credential setup",
          "depth": 0,
          "id": "aws-credential-setup"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your credentials",
          "depth": 1,
          "id": "configure-your-credentials"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "CSV requirements",
          "depth": 1,
          "id": "csv-requirements"
        }, {
          "value": "JSON requirements",
          "depth": 1,
          "id": "json-requirements"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      code: "code",
      strong: "strong",
      pre: "pre",
      span: "span",
      h3: "h3",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components), {NoCommonErrors} = _components;
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Need to join, transform, or filter your S3 data? Consider using the ", _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "AWS\nAthena"
          }), " source to directly query your S3 data using\nSQL."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data from CSV and JSON files stored in an Amazon S3 bucket and push them to downstream destinations. To get started, you need an S3 bucket and AWS credentials."
      }), "\n", _jsx(_components.h2, {
        id: "aws-credential-setup",
        children: "AWS credential setup"
      }), "\n", _jsxs(_components.p, {
        children: ["See the guide for ", _jsx(_components.a, {
          href: "/security/aws",
          children: "configuring AWS credentials"
        }), " to learn how you can set up an IAM principal and use its credentials. The IAM principal whose credentials you use must have programmatic access enabled and permission to read from the S3 path you want to use."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch needs the following IAM actions to retrieve items from your bucket:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Action"
            }), _jsx(_components.th, {
              align: "left",
              children: "Details"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:GetObject"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to retrieve objects from Amazon S3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:ListBucket"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to list some or all the objects in an Amazon S3 bucket (up to 1000)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "sts:GetFederationToken"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "(Optional) Grants permission to generate short-lived AWS credentials for faster warehouse reads"
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you use an ", _jsx(_components.strong, {
            children: "access key"
          }), " credential in Hightouch, we recommend adding\n", _jsx(_components.code, {
            children: "sts:GetFederationToken"
          }), ". This lets Hightouch generate short-lived credentials\nscoped to the S3 prefix being written, which enables syncs to export data from\nyour warehouse to S3 (faster than reading rows via streaming). If you don't\ngrant this permission, Hightouch can still work, but syncs will fall back to\nthe slower streaming method instead. If you use a ", _jsx(_components.strong, {
            children: "cross-account role"
          }), ",\n", _jsx(_components.code, {
            children: "sts:GetFederationToken"
          }), " isn't required."]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can use the following JSON sample to create your IAM policy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: ["{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\":"
          }), "\n    [\n      {\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetObject\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:ListBucket\""
          }), "],\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), "\n          [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::${bucketName}/*\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::${bucketName}\""
          }), "],\n      },\n    ],\n}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "If you want to add the optional permission, include the following statement:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"AllowFederationTokenForS3\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sts:GetFederationToken\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Amazon S3"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-credentials",
        children: "Configure your credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the credentials you previously created or click ", _jsx(_components.strong, {
          children: "Create new"
        }), " to set them up now."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your S3 bucket's ", _jsx(_components.strong, {
          children: "Region"
        }), " and the ", _jsx(_components.strong, {
          children: "Bucket name"
        }), ". The bucket name should be just the name of the bucket, not a URL."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Amazon S3 as a source for the first time, Hightouch validates your AWS credentials and access to your S3 bucket. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from your S3 bucket. The file must have either a ", _jsx(_components.code, {
          children: ".csv"
        }), " or ", _jsx(_components.code, {
          children: ".json"
        }), " extension."]
      }), "\n", _jsx(_components.h3, {
        id: "csv-requirements",
        children: "CSV requirements"
      }), "\n", _jsx(_components.p, {
        children: "CSV files must meet the following requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "They must have a header row. The values in the header row are automatically available as column names when you set up a sync."
        }), "\n", _jsx(_components.li, {
          children: "They must use comma-separated values; tabs and other delimiters aren't supported."
        }), "\n", _jsxs(_components.li, {
          children: ["They must use double quotes (", _jsx(_components.code, {
            children: "\""
          }), ") for quoted values."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "json-requirements",
        children: "JSON requirements"
      }), "\n", _jsx(_components.p, {
        children: "JSON files must meet the following requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The input file must contain an array at the top level."
        }), "\n", _jsx(_components.li, {
          children: "Each element in the input file must be an object with the same keys."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The keys of the array elements are automatically available as column names when you set up a sync."
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing data from a specific file in your S3 bucket as well\nas from the last modified file that matches a given prefix."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models?from=docs",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Amazon S3 source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "relative path"
          }), " to the CSV or JSON file that you want to sync\ndata from, like ", _jsx(_components.code, {
            children: "path/prefix/file.csv"
          }), ". The path shouldn't contain the bucket\nname."]
        }), "\n", _jsxs(_components.li, {
          children: ["To sync the last modified file with a given prefix, check the ", _jsx(_components.strong, {
            children: "Use last\nmodified"
          }), " box and enter a prefix like ", _jsx(_components.code, {
            children: "path/prefix"
          }), " in the path field. See\nAmazon's ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/using-prefixes.html",
            children: "S3 docs on\nprefixes"
          }), "\nfor examples of how prefixes match objects. To sync the last modified file in\nthe entire bucket, check the ", _jsx(_components.strong, {
            children: "Use last modified"
          }), " box and leave the path field\nblank."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
